import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signUp, initAuthTables } from "@/lib/auth";
import { setSessionToken } from "@/lib/sessionStorage";
import { SocialLoginButtons, loginWithGoogle, loginWithFacebook, loginWithApple } from "@/components/SocialLoginButtons";
import { sendWelcomeEmail } from "@/lib/email";
import { AgeVerification, type AgeRestrictions } from "@/components/AgeVerification";
import { TermsOfService } from "@/components/TermsOfService";

const OrganizerSignUp: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Initialize tables on mount
  React.useEffect(() => {
    initAuthTables();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      const result = await signUp(name, email, password);
      
      if (result.success && result.user && result.session_token) {
        // Store session (now using persistent storage utility)
        setSessionToken(result.session_token);
        localStorage.setItem('whipt_user', JSON.stringify(result.user));
        
        // Send welcome email (non-blocking)
        sendWelcomeEmail(email, name).catch(err => {
          console.error('Failed to send welcome email:', err);
          // Don't block sign-up if email fails
        });
        
        // Check if user has already completed onboarding (age + terms)
        const ageAlreadyVerified = localStorage.getItem('whipt_age_verification');
        const termsAlreadyAccepted = localStorage.getItem('whipt_terms_accepted') === 'true';
        
        if (ageAlreadyVerified && termsAlreadyAccepted) {
          // Returning user who already completed everything - go straight to organizer
          console.log('✅ Returning user - skipping age verification and terms');
          localStorage.setItem('whipt_organizer_mode', 'student');
          window.location.href = "/organizer";
        } else {
          // New user or incomplete onboarding - show age verification
          console.log('ℹ️ New user or incomplete onboarding - showing age verification');
          setShowAgeVerification(true);
        }
      } else {
        setError(result.error || "Failed to create account");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgeVerified = (restrictions: AgeRestrictions) => {
    // Age verification complete - check if terms already accepted
    const termsAlreadyAccepted = localStorage.getItem('whipt_terms_accepted') === 'true';
    
    if (restrictions.accountActive) {
      setShowAgeVerification(false);
      
      // Skip terms if already accepted (returning user)
      if (termsAlreadyAccepted) {
        console.log('✅ Terms already accepted, skipping TOS panel');
        localStorage.setItem('whipt_organizer_mode', 'student');
        window.location.href = "/organizer";
      } else {
        console.log('ℹ️ First time user, showing TOS panel');
        setShowTerms(true);
      }
    } else {
      // Account pending parental consent - show message and stay on page
      setShowAgeVerification(false);
      alert("Your account is pending parental consent. You'll receive an email when it's activated.");
    }
  };

  const handleTermsAccepted = () => {
    // Terms accepted - navigate to Student Organizer
    setShowTerms(false);
    localStorage.setItem('whipt_organizer_mode', 'student');
    window.location.href = "/organizer";
  };

  const handleTermsDeclined = () => {
    // User declined terms - log them out
    localStorage.removeItem('whipt_session_token');
    localStorage.removeItem('whipt_user');
    setShowTerms(false);
    setError("You must accept the Terms of Service to use WHIPT");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white flex items-center justify-center px-4 py-8">
      {showAgeVerification && (
        <AgeVerification 
          onVerified={handleAgeVerified}
          onClose={() => {
            // Allow closing only for 13+ users
            setShowAgeVerification(false);
            setShowTerms(true);
          }}
        />
      )}
      {showTerms && (
        <TermsOfService 
          onAccept={handleTermsAccepted}
          onDecline={handleTermsDeclined}
        />
      )}
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8 space-y-4">
          <div className="inline-block">
            <div className="border-4 border-black rounded-lg p-4 bg-white/5 backdrop-blur-sm">
              <img
                src="/pronoia-wellness-hero2.webp"
                alt="Pronoia Wellness"
                className="max-w-xs w-auto object-contain mx-auto"
              />
            </div>
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold mb-1">
              <span className="bg-gradient-to-r from-amber-200 via-orange-300 to-amber-200 bg-clip-text text-transparent">
                Where'd I Put That?
              </span>
            </h1>
            <p className="text-xs uppercase tracking-[0.4em] text-amber-200/60">
              Scholarly Student Organizer
            </p>
          </div>
        </div>

        {/* Sign Up Panel */}
        <div className="rounded-3xl border border-white/15 bg-black/40 backdrop-blur-xl p-8 space-y-6 shadow-[0_25px_120px_rgba(0,0,0,0.7)]">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold">Create Account</h2>
            <p className="text-sm text-slate-300">Join WHIPT and start organizing your academic life.</p>
          </div>

          {error && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block text-sm font-semibold tracking-wide text-slate-100">
              Full Name
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="mt-2 w-full rounded-xl border border-white/20 bg-black/30 px-4 py-3 text-sm focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 outline-none transition-all"
                placeholder="Jane Doe"
                required
                disabled={isLoading}
              />
            </label>

            <label className="block text-sm font-semibold tracking-wide text-slate-100">
              Email
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-2 w-full rounded-xl border border-white/20 bg-black/30 px-4 py-3 text-sm focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 outline-none transition-all"
                placeholder="you@university.edu"
                required
                disabled={isLoading}
              />
            </label>

            <label className="block text-sm font-semibold tracking-wide text-slate-100">
              Password
              <div className="relative mt-2">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full rounded-xl border border-white/20 bg-black/30 px-4 py-3 pr-12 text-sm focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 outline-none transition-all"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 text-sm"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            <label className="block text-sm font-semibold tracking-wide text-slate-100">
              Confirm Password
              <div className="relative mt-2">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="w-full rounded-xl border border-white/20 bg-black/30 px-4 py-3 pr-12 text-sm focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 outline-none transition-all"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 text-sm"
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold py-3 text-sm uppercase tracking-[0.2em] hover:shadow-[0_8px_32px_rgba(251,146,60,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          {/* Social Login Buttons */}
          <SocialLoginButtons 
            onGoogleLogin={async () => {
              try {
                await loginWithGoogle();
              } catch (err) {
                setError(typeof err === 'string' ? err : 'Social login is not yet available. Please use email and password.');
              }
            }}
            onFacebookLogin={async () => {
              try {
                await loginWithFacebook();
              } catch (err) {
                setError(typeof err === 'string' ? err : 'Social login is not yet available. Please use email and password.');
              }
            }}
            onAppleLogin={async () => {
              try {
                await loginWithApple();
              } catch (err) {
                setError(typeof err === 'string' ? err : 'Social login is not yet available. Please use email and password.');
              }
            }}
          />

          <div className="text-center pt-4 space-y-3">
            <p className="text-sm text-slate-400">
              Already have an account?{" "}
              <Link to="/organizer/sign-in" className="text-amber-300 hover:text-amber-200 font-semibold">
                Sign In
              </Link>
            </p>
            <Link 
              to="/" 
              className="block text-xs uppercase tracking-[0.3em] text-white/50 hover:text-white/80 transition-colors"
            >
              ← Back to Landing
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default OrganizerSignUp;
